import {
  ControlledDateOfBirthField,
  ControlledTextField,
} from '@act/storybook';
import { Box } from '@mui/material';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useConsentIdentityForm } from '../UseConsentIdentityForm';
import { datadogPhiMaskProps } from 'toolkit/environment/environment/userMonitoring.util';

interface ConsentIdentityFormProps {
  activeStep: number;
  setActiveStep: (step: number) => void;
  onFormValidityChange?: (isValid: boolean) => void;
}

export interface ConsentIdentityFormRef {
  submitForm: () => Promise<void>;
}

const ConsentIdentityForm = forwardRef<
  ConsentIdentityFormRef,
  ConsentIdentityFormProps
>(
  (
    { activeStep, setActiveStep: _setActiveStep, onFormValidityChange },
    ref
  ) => {
    const { t } = useTranslation();

    const [params] = useSearchParams();
    const key = params.get('key');

    const {
      formState,
      control,
      onSubmit,
    } = useConsentIdentityForm({
      linkKey: key,
    });

    // Expose the onSubmit function to parent component
    useImperativeHandle(
      ref,
      () => ({
        submitForm: onSubmit,
      }),
      [onSubmit]
    );

    // Notify parent of form validity so the Continue button can be disabled when zod validation fails (e.g. invalid date)
    React.useEffect(() => {
      if (onFormValidityChange) {
        const isValid =
          activeStep === 1
            ? true // Step 1: Address - all optional
            : formState.isValid;
        onFormValidityChange(isValid);
      }
    }, [formState.isValid, activeStep, onFormValidityChange]);

    const renderStepContent = () => {
      switch (activeStep) {
        case 0: // Member Identity Confirmation
          return (
            <div key="step-0">
              <div className="identity-form-row">
                <ControlledTextField trackingName="name-input"
                  control={control}
                  label={
                    key ? t('consentForm.name') : t('consentForm.memberName')
                  }
                  name="name"
                  required
                />
              </div>
              <div className="identity-form-row">
                <ControlledTextField trackingName="email-input"
                  control={control}
                  label={
                    key ? t('consentForm.email') : t('consentForm.memberEmail')
                  }
                  name="email"
                />
              </div>
              <div className="identity-form-row">
                <ControlledDateOfBirthField trackingName="date-of-birth-input"
                  control={control}
                  label={t('consentForm.dateOfBirth')}
                  name="dateOfBirth"
                  required
                />
              </div>
            </div>
          );

        case 1: // Address Confirmation
          return (
            <div key="step-1">
              <div className="identity-form-row">
                <ControlledTextField trackingName="line1-input"
                  control={control}
                  label={
                    key
                      ? t('consentForm.streetAddress')
                      : t('consentForm.memberStreetAddress')
                  }
                  name="line1"
                />
              </div>
              <div className="identity-form-row">
                <ControlledTextField trackingName="city-input"
                  control={control}
                  label={t('consentForm.city')}
                  name="city"
                />
              </div>
              <div className="identity-form-row col-2">
                <ControlledTextField trackingName="state-input"
                  control={control}
                  label={t('consentForm.state')}
                  name="state"
                />
                <ControlledTextField trackingName="zip-input"
                  control={control}
                  label={t('consentForm.zip')}
                  name="zip"
                />
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return <Box {...datadogPhiMaskProps}>{renderStepContent()}</Box>;
  }
);

export default ConsentIdentityForm;
